import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureParameterConfirmationMethod,
  ProcedureParameterUsageValueType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientId} from 'notebook/ELNConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {
  BaseTypedValueType,
  Cell,
  RowMetadata,
  Table,
  TableColumnType,
  TableDimension,
  TableReorderLocks,
} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {FloatSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {
  DataParameterTypedValue,
  ParameterUsage,
  ParameterUsages,
  ProcedureDataParameterSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  convertDataParameterTypeToTypedValue,
  convertTypedValueToDataParameterType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsageColumn,
  getParameterUsagesForId,
  makeParameterUsageCell,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {
  cellFloatTypedValue,
  cellText,
  getConfirmationMethodStructuredTableColumn,
  getConfirmationMethodValueFromCell,
  makeConfirmationMethodCell,
  makeMinMaxCell,
  makeSetpointCell,
  makeStringCell,
  makeUnitReferenceCell,
  validationErrorsToEvaluatedCellError,
} from 'procedures/tables/procedureTableAdapterUtils';
import {ProcedureSchemaFieldTypeContext} from 'procedures/tables/ProcedureTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

export interface ProcedureDataParametersConfigTableMetadata extends BaseParameterConfigTableMetadata {}

export interface ProcedureDataParametersConfigTableRowMetadata {
  apiId: string | null;
  procedureDataParameterId: string | null;
  plannedValueId: string | null;
}

export enum DataParameterConfigColumn {
  NAME = 'NAME',
  TYPE = 'TYPE',
  SETPOINT = 'SETPOINT',
  UNITS = 'UNITS',
  MINIMUM = 'MINIMUM',
  MAXIMUM = 'MAXIMUM',
  DESCRIPTION = 'DESCRIPTION',
  CONFIRMATION_METHOD = 'CONFIRMATION_METHOD',
  CONFIRMED_IN = 'CONFIRMED_IN',
  MEASURED_IN = 'MEASURED_IN',
}

const ALL_POSSIBLE_COLUMNS_ORDERED = [
  DataParameterConfigColumn.NAME,
  DataParameterConfigColumn.TYPE,
  DataParameterConfigColumn.SETPOINT,
  DataParameterConfigColumn.UNITS,
  DataParameterConfigColumn.MINIMUM,
  DataParameterConfigColumn.MAXIMUM,
  DataParameterConfigColumn.DESCRIPTION,
  DataParameterConfigColumn.CONFIRMATION_METHOD,
  DataParameterConfigColumn.CONFIRMED_IN,
  DataParameterConfigColumn.MEASURED_IN,
];

const GET_IS_ENABLED_BY_COLUMN: {[c in DataParameterConfigColumn]?: () => boolean} = {
  [DataParameterConfigColumn.CONFIRMATION_METHOD]: () =>
    BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES,
};

export function columnProvenanceId(column: DataParameterConfigColumn): string {
  return `dp_${column}`;
}

const GET_STC_BY_COLUMN: Record<
  DataParameterConfigColumn,
  (metadata: ProcedureDataParametersConfigTableMetadata) => StructuredTableColumn
> = {
  [DataParameterConfigColumn.NAME]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Name',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.NAME),
  }),
  [DataParameterConfigColumn.TYPE]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      name: 'Data type',
      isRequired: true,
      defaultValue: {
        text: '',
        typedValue: convertDataParameterTypeToTypedValue(DEFAULT_DATA_PARAMETER_TYPE),
      },
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    columnType: TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE, // for TS
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.TYPE),
    context: ProcedureSchemaFieldTypeContext.DATA_PARAMETER,
  }),
  [DataParameterConfigColumn.SETPOINT]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Setpoint',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.SETPOINT),
  }),
  [DataParameterConfigColumn.UNITS]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_REFERENCE,
      name: 'Units',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NORMAL,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.UNITS),
  }),
  [DataParameterConfigColumn.MINIMUM]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Min',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MINIMUM),
  }),
  [DataParameterConfigColumn.MAXIMUM]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ROW_DELEGATE,
      name: 'Max',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.NARROW,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MAXIMUM),
  }),
  [DataParameterConfigColumn.DESCRIPTION]: (metadata) => ({
    ...makeStructuredTableColumn({
      columnType: TableColumnType.STRING,
      name: 'Description',
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
    }),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.DESCRIPTION),
  }),
  [DataParameterConfigColumn.CONFIRMED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(DataParameterConfigColumn.CONFIRMED_IN),
      columnGroup: StructuredTableColumnGroups.REFERENCE,
    }),
  [DataParameterConfigColumn.MEASURED_IN]: (metadata) =>
    getParameterUsageColumn({
      metadata,
      provenanceUuid: columnProvenanceId(DataParameterConfigColumn.MEASURED_IN),
      columnName: 'Measured in',
    }),
  [DataParameterConfigColumn.CONFIRMATION_METHOD]: () => ({
    ...getConfirmationMethodStructuredTableColumn(),
    provenanceUuid: columnProvenanceId(DataParameterConfigColumn.CONFIRMATION_METHOD),
  }),
};

export function getColumnIndex(
  targetColumn: DataParameterConfigColumn,
  stColumns: Array<StructuredTableColumn>
): number {
  return stColumns.findIndex(({provenanceUuid}) => provenanceUuid === columnProvenanceId(targetColumn));
}

/**
 * This table adapter is for configuration of parameters
 */
export type ProcedureDataParametersConfigTable = BaseStructuredTable<
  ProcedureDataParametersConfigTableMetadata,
  ProcedureDataParametersConfigTableRowMetadata,
  ClientId
>;

function isFloatTypedValueEmpty(floatTypedValue: FloatSchemaFieldTypedValue | null): boolean {
  return floatTypedValue == null || floatTypedValue.value == null || floatTypedValue.value === '';
}

export default class ProcedureDataParametersConfigTableAdapter extends GenericStructuredTableAdapter<
  ProcedureDataParametersConfigTableMetadata,
  ProcedureDataParametersConfigTableRowMetadata
> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG, {allowNoRowsInEntries: true, ...options});
  }

  override getActionFunctions(
    tableMetadata: ProcedureDataParametersConfigTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<
    ProcedureDataParametersConfigTableMetadata,
    ProcedureDataParametersConfigTableRowMetadata
  > {
    return {secondary: [downloadCSVActionFunction]};
  }

  getDisplayName(table: ProcedureDataParametersConfigTable): string {
    return '';
  }

  getRowDisplayName(_: ProcedureDataParametersConfigTable): string {
    return 'parameter';
  }

  getBlankRowMetadata(
    tableMetadata: ProcedureDataParametersConfigTableMetadata
  ): ProcedureDataParametersConfigTableRowMetadata {
    return {apiId: null, procedureDataParameterId: null, plannedValueId: null};
  }

  static getAdapter(): ProcedureDataParametersConfigTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG
    ] as ProcedureDataParametersConfigTableAdapter;
  }

  override allowPasteTable(): boolean {
    return false;
  }

  override allowDeleteTable(_: ProcedureDataParametersConfigTable): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureDataParametersConfigTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const enabledColumns = ALL_POSSIBLE_COLUMNS_ORDERED.filter(
      (c) => GET_IS_ENABLED_BY_COLUMN[c]?.() ?? true
    );
    return enabledColumns.map((c) => GET_STC_BY_COLUMN[c](metadata));
  }

  // We want to keep our columns editable even after the parameter is submitted and persisted
  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      ...super.getNonFinalizingColumnTypes(),
      TableColumnType.STRING,
      TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE,
      TableColumnType.CUSTOM,
      TableColumnType.UNIT_REFERENCE,
      TableColumnType.OPTION_SELECTOR,
      TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    ];
  }

  // Check against all user-entered fields. Will never report "empty" if there is a backing model;
  // this deliberately prevents users from blanking out fields on an existing object.
  static isDataParameterSettingEmpty(p: ProcedureDataParameterSetting): boolean {
    const setpoint = p.values[0];
    return (
      p.dataParameterVersion.id == null &&
      p.dataParameterVersion.name.length === 0 &&
      isFloatTypedValueEmpty(p.dataParameterVersion.minimum) &&
      isFloatTypedValueEmpty(p.dataParameterVersion.maximum) &&
      p.dataParameterVersion.description.length === 0 &&
      formatTypedValue(setpoint.value).length === 0 &&
      p.dataParameterVersion.confirmationMethod === ProcedureParameterConfirmationMethod.TABLE
    );
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureDataParametersConfigTable,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>
  ): boolean {
    const setting = ProcedureDataParametersConfigTableAdapter.rowToDataParameterSetting(
      structuredTable.columns,
      row
    );
    return ProcedureDataParametersConfigTableAdapter.isDataParameterSettingEmpty(setting);
  }

  static getDelegateColumnByColumnAndDataType(
    this: void,
    dependentColumn: StructuredTableColumn,
    dataType: DataParameterType
  ): StructuredTableColumn {
    switch (dataType.type) {
      case FieldType.DATETIME:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: datetime',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.DATETIME},
        };
      case FieldType.TEXT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: text',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          // The provenance ID must be stable. It's used to track the LSUV for any cell which uses this column
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.TEXT},
        };
      case FieldType.FLOAT:
        return {
          ...makeStructuredTableColumn({
            columnType: TableColumnType.CUSTOM,
            name: 'Delegate: float',
            isRequired: false,
          }),
          columnType: TableColumnType.CUSTOM,
          provenanceUuid: `${dependentColumn.provenanceUuid}_${dataType.type}`,
          customColumnConfig: {dataType: CustomColumnDataType.FLOAT},
        };
      default:
        assert(false, 'Unknown data type', dataType);
    }
  }

  static makeCellFromDataParameter(
    dataParameterSetting: ProcedureDataParameterSetting,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>
  ): Cell {
    const minMaxCellError = (): Optional<EvaluatedCellError> =>
      validationErrorsToEvaluatedCellError(
        dataParameterSetting.dataParameterVersion.cid,
        ProcedureValidationObjectType.DATA_PARAMETER,
        filterProcedureValidationErrors(validationErrors, {
          errorTypes: [ProcedureValidationErrorType.INVALID_PARAMETER_RANGE],
        })
      );

    switch (column.provenanceUuid) {
      case columnProvenanceId(DataParameterConfigColumn.NAME): {
        const nameCellError = validationErrorsToEvaluatedCellError(
          dataParameterSetting.dataParameterVersion.cid,
          ProcedureValidationObjectType.DATA_PARAMETER,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [
              ProcedureValidationErrorType.NON_UNIQUE_NAME,
              ProcedureValidationErrorType.INVALID_NAME,
            ],
          })
        );
        return makeStringCell(dataParameterSetting.dataParameterVersion.name, {error: nameCellError});
      }

      case columnProvenanceId(DataParameterConfigColumn.TYPE):
        return {
          text: '',
          typedValue: convertDataParameterTypeToTypedValue(
            dataParameterSetting.dataParameterVersion.dataType
          ),
        };

      case columnProvenanceId(DataParameterConfigColumn.SETPOINT): {
        const setpointNotInRangeError = (): Optional<EvaluatedCellError> =>
          validationErrorsToEvaluatedCellError(
            dataParameterSetting.dataParameterVersion.cid,
            ProcedureValidationObjectType.DATA_PARAMETER,
            filterProcedureValidationErrors(validationErrors, {
              errorTypes: [ProcedureValidationErrorType.SETPOINT_NOT_IN_RANGE],
            })
          );
        return makeSetpointCell(dataParameterSetting, {
          error: setpointNotInRangeError(),
        });
      }

      case columnProvenanceId(DataParameterConfigColumn.UNITS):
        return makeUnitReferenceCell(dataParameterSetting);

      case columnProvenanceId(DataParameterConfigColumn.MINIMUM):
        return makeMinMaxCell(dataParameterSetting, dataParameterSetting.dataParameterVersion.minimum, {
          error: minMaxCellError(),
        });

      case columnProvenanceId(DataParameterConfigColumn.MAXIMUM):
        return makeMinMaxCell(dataParameterSetting, dataParameterSetting.dataParameterVersion.maximum, {
          error: minMaxCellError(),
        });

      case columnProvenanceId(DataParameterConfigColumn.DESCRIPTION):
        return makeStringCell(dataParameterSetting.dataParameterVersion.description);

      case columnProvenanceId(DataParameterConfigColumn.CONFIRMED_IN):
        return makeParameterUsageCell(parameterUsages, {
          confirmationMethod: dataParameterSetting.dataParameterVersion.confirmationMethod,
        });

      case columnProvenanceId(DataParameterConfigColumn.MEASURED_IN):
        return makeParameterUsageCell(parameterUsages, {
          usageType: ProcedureParameterUsageValueType.MEASUREMENT,
          confirmationMethod: dataParameterSetting.dataParameterVersion.confirmationMethod,
        });

      case columnProvenanceId(DataParameterConfigColumn.CONFIRMATION_METHOD):
        return makeConfirmationMethodCell(
          dataParameterSetting.dataParameterVersion.confirmationMethod ??
            // Adding a new row sets every cell to an empty typed value, we want it to default to table
            ProcedureParameterConfirmationMethod.TABLE
        );

      default:
        assert(false, 'Unknown table column', {column});
    }
  }

  static dataParameterToRow(
    dataParameterSetting: ProcedureDataParameterSetting,
    table: ProcedureDataParametersConfigTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>,
    cellLockedMessage?: Optional<string>
  ): StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata> {
    const adapter = ProcedureDataParametersConfigTableAdapter.getAdapter();

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: dataParameterSetting.dataParameterVersion.cid,
      metadata: {
        apiId: dataParameterSetting.dataParameterVersion.id,
        procedureDataParameterId: dataParameterSetting.dataParameterVersion.procedureDataParameterId,
        plannedValueId: dataParameterSetting.values.length > 0 ? dataParameterSetting.values[0].id : null,
      },
      cells: table.columns.map((column) =>
        ProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
          dataParameterSetting,
          column,
          validationErrors,
          parameterUsages
        )
      ),
    };
  }

  static rowToDataParameterSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>
  ): ProcedureDataParameterSetting {
    assert(
      columns.length === row.cells.length,
      'Expected row to have same number of cells as number of columns'
    );

    const setpointCell = row.cells[getColumnIndex(DataParameterConfigColumn.SETPOINT, columns)];

    const getDataParameterTypedValue = (dataType: DataParameterType): DataParameterTypedValue => {
      const setpointCellType = setpointCell.typedValue?.type;
      const selectedType = dataType.type;

      let inferredTypedValue: DataParameterTypedValue;
      if (setpointCellType == null) {
        // Setpoint typed value has not been set, so infer the typed value from the selected type
        if (selectedType === FieldType.TEXT) {
          inferredTypedValue = {type: FieldType.TEXT, value: setpointCell.text};
        } else if (selectedType === FieldType.DATETIME) {
          inferredTypedValue = {
            type: FieldType.DATETIME,
            value: setpointCell.text,
            datetimeValue: null,
          };
        } else if (selectedType === FieldType.FLOAT) {
          inferredTypedValue = {
            type: FieldType.FLOAT as FieldType.FLOAT,
            value: setpointCell.text,
            rawValue: setpointCell.text,
            floatValue: null,
          };
        } else {
          assert(false, 'Unknown data type selected', {selectedType});
        }
      } else if (selectedType === setpointCellType) {
        // User has not changed type selector, so use the setpoint typed value
        inferredTypedValue = setpointCell.typedValue!;
      } else if (selectedType === FieldType.TEXT) {
        // User updated the type selector to text, so convert the setpoint typed value
        inferredTypedValue = {type: FieldType.TEXT, value: setpointCell.text};
      } else if (selectedType === FieldType.DATETIME) {
        // User updated the type selector to datetime, so convert the setpoint typed value
        inferredTypedValue = {
          type: FieldType.DATETIME,
          value: setpointCell.text,
          datetimeValue: null,
        };
      } else if (selectedType === FieldType.FLOAT) {
        // User updated the type selector to float, so convert the setpoint typed value
        inferredTypedValue = {
          type: FieldType.FLOAT,
          value: setpointCell.text,
          rawValue: setpointCell.text,
          floatValue: null,
        };
      } else {
        assert(false, 'Unknown data type selected', {selectedType});
      }
      return inferredTypedValue;
    };

    const paramTypeCell = row.cells[getColumnIndex(DataParameterConfigColumn.TYPE, columns)];
    const dataType =
      paramTypeCell.typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE
        ? convertTypedValueToDataParameterType(paramTypeCell.typedValue)
        : DEFAULT_DATA_PARAMETER_TYPE;

    const unitReferenceCell = row.cells[getColumnIndex(DataParameterConfigColumn.UNITS, columns)];
    if (
      dataType.type === FieldType.FLOAT &&
      unitReferenceCell.typedValue?.type === BaseTypedValueType.UNIT_REFERENCE
    ) {
      dataType.unit = unitReferenceCell.typedValue.unitSummary;
    }

    const minimum = cellFloatTypedValue(
      row.cells[getColumnIndex(DataParameterConfigColumn.MINIMUM, columns)],
      dataType.type
    );
    const maximum = cellFloatTypedValue(
      row.cells[getColumnIndex(DataParameterConfigColumn.MAXIMUM, columns)],
      dataType.type
    );

    const confirmationMethod = BenchlingConfig.PROCEDURES_ENABLE_USE_PARAMETERS_IN_PLATES
      ? getConfirmationMethodValueFromCell(
          row.cells[getColumnIndex(DataParameterConfigColumn.CONFIRMATION_METHOD, columns)]
        )
      : ProcedureParameterConfirmationMethod.TABLE;
    return {
      // TODO(BNCH-86722): include limits and description
      dataParameterVersion: {
        cid: row.rowId,
        id: row.metadata.apiId,
        procedureDataParameterId: row.metadata.procedureDataParameterId,
        name: cellText(row.cells[getColumnIndex(DataParameterConfigColumn.NAME, columns)]).trim(),
        description: cellText(
          row.cells[getColumnIndex(DataParameterConfigColumn.DESCRIPTION, columns)]
        ).trim(),
        dataType,
        minimum,
        maximum,
        confirmationMethod,
        // Data parameters with library parameter definitions are handled by a separate adapter
        libraryParameterDefinition: null,
      },
      values: [
        {
          id: row.metadata.plannedValueId,
          value: getDataParameterTypedValue(dataType),
          cid: `${row.rowId}-planned-value`,
        },
      ],
    };
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    dataParameterSetting: ProcedureDataParameterSetting,
    row: StructuredTableRow<ProcedureDataParametersConfigTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    return columns.map((column) => {
      const delegatedColumnProvenanceId = (row.metadata as RowMetadata).delegatedColumns?.[
        column.provenanceUuid
      ];
      return {
        columnGroup: null,
        columnType: column.columnType,
        columnProvenanceUuid: delegatedColumnProvenanceId ?? column.provenanceUuid,
        submittedAt,
        fieldId: null,
        value:
          ProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
            dataParameterSetting,
            column,
            [],
            parameterUsages
          ).typedValue ?? null,
      };
    });
  }

  override toTable(parametersConfigTable: ProcedureDataParametersConfigTable): Table {
    const table = super.toTable(parametersConfigTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  createTableFromDataParameters(
    createProps: CreateStructuredTableProps<ProcedureDataParametersConfigTableMetadata>,
    tableCid: string,
    dataParameters: Array<ProcedureDataParameterSetting>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages
  ): ProcedureDataParametersConfigTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: dataParameters.map((d, index) =>
        ProcedureDataParametersConfigTableAdapter.dataParameterToRow(
          d,
          baseTable,
          index,
          procedureValidationErrors,
          getParameterUsagesForId(parameterUsagesByParameterVersionId, d.dataParameterVersion.id),
          null
        )
      ),
    };
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
